import type { BoxProps, TypographyProps } from '@mui/material';
import { useTheme } from '@mui/material';
import { IconButton, Stack } from '@mui/material';
import { useSpring } from '@react-spring/web';
import type {
  NotificationAction as NotificationUIAction,
  NotificationIconButtonAction,
} from '@schooly/style';
import {
  CheckIcon,
  NOTIFICATION_GAP,
  NOTIFICATION_HEIGHT,
  NotificationButton,
  Toast,
} from '@schooly/style';
import type React from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import { NotificationToastContainer } from './ToastContainer.styled';

export type ButtonColor = 'light' | 'white-text';

export type NotificationId = string | number;

export interface NotificationButtonAction {
  textId: string;
  handler: VoidFunction;
  buttonColor?: ButtonColor;
  isOutline?: boolean;
}

export type NotificationAction = NotificationButtonAction | NotificationIconButtonAction;

export type NotificationActions = NotificationAction[];

export interface NotificationProps {
  message?: string;
  textId?: string;
  // Dynamic values for `react-intl` messages
  values?: Record<string, any>;
  type?: 'success' | 'error' | 'info';
  icon?: React.ReactElement;
  id?: NotificationId;
  persistent?: boolean;
  actions?: NotificationActions;
}

export const Notification: FC<
  {
    notification: NotificationProps;
    toast?: boolean;
    typographyProps?: TypographyProps;
  } & BoxProps
> = ({ notification, toast, typographyProps, ...props }) => {
  const { actions, icon, message, textId, type, values } = notification;

  const { $t } = useIntl();
  const theme = useTheme();

  const fadeInFromBottom = useSpring({
    from: {
      marginTop: -NOTIFICATION_HEIGHT,
      translateY: 240,
      opacity: 0,
    },
    to: {
      marginTop: NOTIFICATION_GAP,
      translateY: 0,
      opacity: 1,
    },
    config: {
      tension: 600,
      friction: 50,
      mass: 1,
    },
  });

  const finalMessage =
    textId && textId.trim && textId.trim()
      ? $t({ id: textId, defaultMessage: message }, values)
      : message;

  if (!finalMessage || !(finalMessage.trim && finalMessage.trim())) {
    return null;
  }

  const resolvedIcon = type === 'success' ? <CheckIcon fill="white" /> : icon;
  const whiteColor = theme.palette.common.white;

  const startIcon = resolvedIcon ? (
    <Stack
      sx={{
        color: whiteColor,
        '& .svg-icon': {
          color: whiteColor,
          '--icon-size': '1rem',
          marginRight: '0.5rem',
        },
      }}
    >
      {resolvedIcon}
    </Stack>
  ) : undefined;

  const backgroundColor = type === 'error' ? theme.palette.error.main : theme.palette.primary.main;

  const resolvedActions: NotificationUIAction[] | undefined = actions?.map((action) =>
    'icon' in action
      ? action
      : {
          label: $t({ id: action.textId }),
          handler: action.handler,
          buttonVariant:
            action.isOutline || action.buttonColor === 'white-text'
              ? ('outlined' as const)
              : ('filled' as const),
        },
  );

  const actionsSlot = resolvedActions && (
    <Stack direction="row" gap={1} ml={2}>
      {resolvedActions.map((action, index) => {
        const key = `action-${index}`;
        return 'icon' in action ? (
          <IconButton
            key={key}
            sx={{
              color: whiteColor,
              '& .svg-icon': {
                m: 0,
                color: whiteColor,
              },
            }}
            onClick={action.handler}
          >
            {action.icon}
          </IconButton>
        ) : (
          <NotificationButton
            key={key}
            buttonVariant={action.buttonVariant}
            onClick={action.handler}
          >
            {action.label}
          </NotificationButton>
        );
      })}
    </Stack>
  );

  return toast ? (
    <NotificationToastContainer style={fadeInFromBottom}>
      <Toast
        message={finalMessage}
        startIcon={startIcon}
        backgroundColor={backgroundColor}
        textColor={whiteColor}
        actionsSlot={actionsSlot}
        typographyProps={typographyProps}
        data-test-id="notification"
        {...props}
      />
    </NotificationToastContainer>
  ) : (
    <Toast
      message={finalMessage}
      startIcon={startIcon}
      backgroundColor={backgroundColor}
      textColor={whiteColor}
      actionsSlot={actionsSlot}
      typographyProps={typographyProps}
      data-test-id="notification"
      {...props}
    />
  );
};
